import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import api from '../../services/api';
import AdminLogoHeader from '../../components/admin/AdminLogoHeader';

const formatDateTime = (value) => {
  if (!value) return '-';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return value;
  return dt.toLocaleString();
};

const DetailRow = ({ label, value }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 py-2 border-b border-slate-100 last:border-b-0">
    <span className="text-sm font-medium text-slate-500 uppercase tracking-wide">{label}</span>
    <span className="sm:col-span-2 text-sm text-slate-800 break-words">{value || '-'}</span>
  </div>
);

const CompliancePreview = () => {
  const { pendingId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const data = await api.adminGetPendingUserDetail(pendingId);
        if (!mounted) return;
        setDetail(data);
        setNote(data?.admin_note || '');
      } catch (err) {
        if (mounted) setError(err.message || 'Unable to load applicant details.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [pendingId]);

  const handleApprove = async () => {
    if (!detail) return;
    try {
      setSubmitting(true);
      await api.adminApprovePendingUser(detail.id, { note });
      navigate('/admin/compliance', { replace: true });
    } catch (err) {
      alert(err.message || 'Failed to approve applicant.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    if (!detail) return;
    const confirmed = window.confirm('Decline this onboarding request?');
    if (!confirmed) return;
    try {
      setSubmitting(true);
      await api.adminRejectPendingUser(detail.id, { note });
      navigate('/admin/compliance', { replace: true });
    } catch (err) {
      alert(err.message || 'Failed to decline applicant.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminLogoHeader />
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-sm text-emerald-600 hover:underline"
        >
          &larr; Back to compliance approvals
        </button>

        <section className="bg-white rounded-2xl shadow-sm border border-slate-100">
          <div className="border-b border-slate-100 px-6 py-4">
            <h1 className="text-xl font-semibold text-slate-900">Applicant preview</h1>
            <p className="text-sm text-slate-500">Review the submitted KYC data and supporting documents.</p>
          </div>

          {loading ? (
            <p className="px-6 py-6 text-sm text-slate-500">Loading applicant details...</p>
          ) : error ? (
            <p className="px-6 py-6 text-sm text-red-600">{error}</p>
          ) : !detail ? (
            <p className="px-6 py-6 text-sm text-slate-500">Applicant not found.</p>
          ) : (
            <>
              <div className="px-6 py-4 grid gap-4 md:grid-cols-2">
                <div className="rounded-xl border border-slate-200">
                  <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-600 uppercase tracking-wide">
                    Personal details
                  </div>
                  <div className="px-4">
                    <DetailRow label="Full name" value={detail.full_name} />
                    <DetailRow label="Preferred names" value={[detail.first_name, detail.middle_name, detail.last_name].filter(Boolean).join(' ')} />
                    <DetailRow label="Contact" value={detail.contact} />
                    <DetailRow label="Phone number" value={detail.phone_number} />
                    <DetailRow label="Email / username" value={detail.contact} />
                    <DetailRow label="User type" value={detail.user_type} />
                    <DetailRow label="Date of birth" value={formatDateTime(detail.date_of_birth)} />
                    <DetailRow label="Address" value={detail.residential_address} />
                    <DetailRow label="Country" value={detail.country} />
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200">
                  <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-600 uppercase tracking-wide">
                    Compliance metadata
                  </div>
                  <div className="px-4">
                    <DetailRow label="Status" value={detail.status} />
                    <DetailRow label="Status since" value={formatDateTime(detail.status_updated_at || detail.updated_at)} />
                    {detail.rejected_at ? (
                      <DetailRow label="Rejected" value={formatDateTime(detail.rejected_at)} />
                    ) : null}
                    <DetailRow label="Verified via OTP" value={detail.is_verified ? 'Yes' : 'No'} />
                    <DetailRow label="KYC admin note" value={detail.admin_note} />
                    <DetailRow label="ID type" value={detail.id_type} />
                    <DetailRow label="ID number" value={detail.id_number} />
                    <DetailRow label="CAC number" value={detail.cac_number} />
                    <DetailRow label="Account verified" value={detail.account_verified ? 'Yes' : 'No'} />
                    <DetailRow label="KYC approved" value={detail.kyc_approved ? 'Yes' : 'No'} />
                    <DetailRow label="Created" value={formatDateTime(detail.created_at)} />
                    <DetailRow label="Last updated" value={formatDateTime(detail.updated_at)} />
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200">
                  <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-600 uppercase tracking-wide">
                    Banking details
                  </div>
                  <div className="px-4">
                    <DetailRow label="Bank name" value={detail.bank_name} />
                    <DetailRow label="Account name" value={detail.bank_account_name} />
                    <DetailRow label="Account number" value={detail.bank_account_number} />
                    <DetailRow label="BVN" value={detail.bvn} />
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 space-y-4">
                <div>
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Notes</h2>
                  <textarea
                    className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
                    rows={4}
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    placeholder="Add or update compliance notes"
                  />
                </div>

              <div>
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-600 mb-2">Documents</h2>
                <ul className="space-y-2 text-sm text-slate-700">
                  {Array.isArray(detail.pending_documents) && detail.pending_documents.length ? (
                    detail.pending_documents.map((doc) => (
                      <li key={`pending-${doc.id}`}>
                        {doc.label || doc.doc_type}:{' '}
                        {doc.file_url ? (
                          <a
                            href={doc.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-emerald-600 hover:underline"
                          >
                            {doc.filename || 'View'}
                          </a>
                        ) : (
                          <span className="text-slate-500">{doc.filename || 'Unavailable'}</span>
                        )}
                      </li>
                    ))
                  ) : null}
                  {detail.id_document_url && (
                    <li>
                      Government ID:{' '}
                        <a href={detail.id_document_url} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">
                          View
                        </a>
                      </li>
                    )}
                    {detail.cac_document_url && (
                      <li>
                        CAC document:{' '}
                        <a href={detail.cac_document_url} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">
                          View
                        </a>
                      </li>
                    )}
                    {Array.isArray(detail.documents) && detail.documents.length ? (
                      detail.documents.map((doc) => (
                        <li key={doc.id}>
                          {doc.doc_type}:{' '}
                          {doc.file_url ? (
                            <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">
                              {doc.filename || 'View'}
                            </a>
                          ) : (
                            <span className="text-slate-500">{doc.filename || 'Unavailable'}</span>
                          )}
                        </li>
                      ))
                    ) : (
                      <li className="text-slate-500">No uploaded documents attached.</li>
                    )}
                  </ul>
                </div>
              </div>

              {detail.status !== 'rejected' ? (
                <div className="flex flex-col gap-3 px-6 py-4 border-t border-slate-100 md:flex-row md:justify-end">
                  <button
                    type="button"
                    onClick={handleDecline}
                    disabled={submitting}
                    className="rounded-lg border border-red-200 px-4 py-2 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                  >
                    {submitting ? 'Processing...' : 'Decline'}
                  </button>
                  <button
                    type="button"
                    onClick={handleApprove}
                    disabled={submitting}
                    className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {submitting ? 'Processing...' : 'Approve'}
                  </button>
                </div>
              ) : (
                <p className="border-t border-slate-100 px-6 py-4 text-sm text-slate-500">
                  This application was rejected. Submit the contact again through manual onboarding to update and reopen it.
                </p>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default CompliancePreview;
